/**
 * The GLSL preamble prepended to an ISF body so it compiles in our renderer.
 *
 * Deliberately close to what VIDOS emits (`ISFParser::GetISFPreprocessor`) so a
 * file behaves the same on the web and on the hardware. The differences are the
 * ones VIDOS should adopt, not divergences to live with:
 *
 *  - `isf_FragNormCoord` is derived from `gl_FragCoord`, not from a `tcoord`
 *    varying, which removes the dependency on a particular vertex shader.
 *  - `IMG_PIXEL` and `IMG_SIZE` are handled by a source rewrite (`rewrite.ts`)
 *    rather than the non-standard `IMG_SIZE_<name>` macro, so shaders written
 *    for VDMX or Resolume compile unchanged.
 */

import type { ISFDoc } from './types';
import { samplerInputs, glslTypeFor } from './inputs';
import { sizeUniformName } from './rewrite';

/**
 * Names the host declares. Both origins are bottom-left, so no Y-flip is needed
 * anywhere between Shadygram and ISF — `getUV()` was already
 * `gl_FragCoord.xy / u_resolution`, which is exactly `isf_FragNormCoord`.
 */
const HOST_UNIFORMS = [
  'uniform vec2 RENDERSIZE;',
  'uniform float TIME;',
  'uniform float TIMEDELTA;',
  'uniform vec4 DATE;',
  'uniform int FRAMEINDEX;',
  'uniform int PASSINDEX;',
];

const MACROS = [
  '#define isf_FragNormCoord (gl_FragCoord.xy / RENDERSIZE)',
  '#define IMG_NORM_PIXEL(sampler, coord) texture2D(sampler, coord)',
  '#define IMG_THIS_PIXEL(sampler) texture2D(sampler, isf_FragNormCoord)',
  '#define IMG_THIS_NORM_PIXEL(sampler) texture2D(sampler, isf_FragNormCoord)',
];

const PRECISION = [
  '#ifdef GL_FRAGMENT_PRECISION_HIGH',
  'precision highp float;',
  '#else',
  'precision mediump float;',
  '#endif',
];

/**
 * The macro Shadygram's extension block defines. A shader wraps its use of the
 * extensions in `#ifdef SHADYGRAM_HOST … #endif`, and every other host simply
 * skips that code, so one file is both a full Shadygram shader and plain ISF.
 */
export const SHADYGRAM_HOST_MACRO = 'SHADYGRAM_HOST';

export interface PreambleOptions {
  /**
   * Host-specific GLSL prepended before the ISF declarations — on Shadygram,
   * the camera/MediaPipe/media-texture uniforms and helpers.
   *
   * The core stays agnostic about what this contains: ISF has no concept of
   * host extensions, so deciding what to offer is the application's business,
   * not the format's. Shaders that use them still export as valid ISF; the
   * linter is what warns that they will not behave the same elsewhere.
   */
  extensions?: string;
}

export interface PreambleResult {
  code: string;
  /** Lines prepended above the author's first line, for error remapping. */
  lineCount: number;
  /** Sampler uniform names, in binding order. */
  samplers: string[];
}

/** Every sampler the document needs: inputs, imported images, pass targets. */
function samplerNames(doc: ISFDoc): string[] {
  const names: string[] = [];
  for (const input of samplerInputs(doc)) names.push(input.NAME);
  for (const name of Object.keys(doc.IMPORTED ?? {})) names.push(name);
  for (const pass of doc.PASSES ?? []) {
    if (pass.TARGET && !names.includes(pass.TARGET)) names.push(pass.TARGET);
  }
  return names;
}

/** Build the preamble for a parsed ISF document. */
export function buildISFPreamble(doc: ISFDoc, options: PreambleOptions = {}): PreambleResult {
  const lines: string[] = [
    ...(options.extensions ? options.extensions.split('\n') : PRECISION),
    '',
    '// ISF host uniforms',
    ...HOST_UNIFORMS,
    '',
    ...MACROS,
    '',
  ];

  const declared = new Set<string>();
  const inputLines: string[] = [];

  for (const input of doc.INPUTS ?? []) {
    if (declared.has(input.NAME)) continue;
    declared.add(input.NAME);
    inputLines.push(`uniform ${glslTypeFor(input.TYPE)} ${input.NAME};`);
  }

  if (inputLines.length) {
    lines.push('// ISF inputs', ...inputLines, '');
  }

  const samplers = samplerNames(doc);
  const sizeLines: string[] = [];

  for (const name of samplers) {
    if (!declared.has(name)) {
      declared.add(name);
      sizeLines.push(`uniform sampler2D ${name};`);
    }
    // IMG_SIZE(x) rewrites to x_size, so every sampler needs its dimensions.
    // The runtime seeds these to vec2(1.0) rather than zero: a missed update
    // then produces wrong scaling instead of a division by zero.
    sizeLines.push(`uniform vec2 ${sizeUniformName(name)};`);
  }

  if (sizeLines.length) {
    lines.push('// ISF image dimensions', ...sizeLines, '');
  }

  const code = lines.join('\n');
  // Derived from the emitted string rather than counting entries: the preamble
  // ends with a blank line that merges with the body's first line, and a
  // hand-maintained count is exactly the kind of thing that silently drifts and
  // makes every reported compile-error line wrong.
  const lineCount = code.split('\n').length - 1;
  return { code, lineCount, samplers };
}
